export const resolveReasonPresetTemplateId = (...candidates) => {
    const match = candidates
        .map(value => Number(value))
        .find(id => Number.isFinite(id) && id > 0)
    return match || null
}

export const isReasonPresetEnabled = setting => {
    if (!setting || typeof setting !== 'object') return false
    const value = setting.IsEnabled ?? setting.isEnabled
    return value === true || value === 1 || String(value).toLowerCase() === 'true'
}

export const activeReasonPresets = option => {
    const list = option?.ReasonPresets ?? option?.reasonPresets
    if (!Array.isArray(list)) return []
    return list
        .filter(reason => reason && reason.IsActive !== false)
        .filter(reason => String(reason.ReasonText || '').trim())
        .slice()
        .sort(
            (a, b) =>
                (Number(a.DisplayOrder) || 0) - (Number(b.DisplayOrder) || 0),
        )
}

export const shouldOpenReasonPopup = (setting, presets) =>
    isReasonPresetEnabled(setting) &&
    Array.isArray(presets) &&
    presets.length > 0

export const responseOptionKey = option =>
    String(
        option?.ResponseId ??
            option?.ResponseUId ??
            option?.ResponseUid ??
            option?.Id ??
            '',
    )

export const reasonPresetTransition = (setting, previousOptions, nextOptions) => {
    if (!isReasonPresetEnabled(setting)) return { action: 'none' }
    const previous = (previousOptions || []).filter(option =>
        shouldOpenReasonPopup(setting, activeReasonPresets(option)),
    )
    const next = (nextOptions || []).filter(option =>
        shouldOpenReasonPopup(setting, activeReasonPresets(option)),
    )
    const previousIds = previous.map(responseOptionKey)
    const nextIds = next.map(responseOptionKey)
    const same =
        previousIds.length === nextIds.length &&
        previousIds.every(id => nextIds.includes(id))

    if (same) {
        if (next.length === 1 && previous.length === 1) {
            return { action: 'open', option: next[0], clearFirst: false }
        }
        return { action: 'none' }
    }

    if (!next.length) {
        return previous.length
            ? { action: 'clear' }
            : { action: 'none' }
    }

    const added =
        next.find(option => !previousIds.includes(responseOptionKey(option))) ||
        next[0]
    return {
        action: 'open',
        option: added,
        clearFirst: previous.length > 0,
    }
}

export const questionPlainText = question => {
    const raw =
        question?.Question ||
        question?.QuestionText ||
        question?.FAQ ||
        ''
    return String(raw)
        .replace(/<[^>]+>/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
}

export const answerNeedsReasonEditor = answer =>
    answer?.IsNegative === true ||
    (Array.isArray(answer?.Reasons) && answer.Reasons.length > 0)

export const getReasonPresetErrorMessage = err => {
    const data = err?.error ?? err?.response?.data ?? err
    if (typeof data === 'string' && data.trim()) return data
    return (
        data?.ErrorMessage ||
        data?.errorMessage ||
        data?.Message ||
        data?.message ||
        err?.message ||
        'Something went wrong'
    )
}

export const formatGenerateSummary = result => {
    if (!result) return 'Reason presets generated.'
    const parts = [
        `${Number(result.ReasonsInserted) || 0} reasons saved`,
        `${Number(result.Generated) || 0} generated`,
        `${Number(result.CopiedFromLibrary) || 0} copied from the library`,
    ]
    if (Number(result.SkippedExisting) > 0) {
        parts.push(`${result.SkippedExisting} already had reasons`)
    }
    if (Number(result.Failed) > 0) {
        parts.push(`${result.Failed} failed`)
    }
    if (result.ErrorMessage) parts.push(result.ErrorMessage)
    return parts.join('. ')
}

export const defaultReasonPresetSetting = templateId => ({
    TemplateId: templateId ?? null,
    IsEnabled: true,
    UseAI: true,
    RequireReason: false,
    AllowOther: true,
    MaxChips: 8,
    LanguageCode: null,
    ResolvedFrom: null,
})

export const normalizeReasonPresetSetting = (setting, templateId) => {
    const source = setting && typeof setting === 'object' ? setting : {}
    const max = Number(source.MaxChips)
    return {
        TemplateId:
            source.TemplateId === undefined ? templateId ?? null : source.TemplateId,
        IsEnabled: source.IsEnabled !== false,
        UseAI: source.UseAI !== false,
        RequireReason: source.RequireReason === true,
        AllowOther: source.AllowOther !== false,
        MaxChips: Number.isFinite(max) ? Math.min(20, Math.max(1, max)) : 8,
        LanguageCode: source.LanguageCode ?? null,
        ResolvedFrom: source.ResolvedFrom || null,
        CompanyId: source.CompanyId,
    }
}

const reasonNoteIds = new Map()

export const rememberReasonNoteId = (questionId, noteId) => {
    if (questionId == null || noteId == null) return
    reasonNoteIds.set(String(questionId), noteId)
}

export const storedReasonNoteId = questionId =>
    reasonNoteIds.get(String(questionId)) ?? null

export const findExistingReasonNote = (question, presets) => {
    const notes = Array.isArray(question?.Notes) ? question.Notes : []
    const storedId = reasonNoteIds.get(String(question?.Id))
    if (storedId != null) {
        const stored = notes.find(
            note => note?.NoteId === storedId || note?.Id === storedId,
        )
        if (stored) return stored
    }
    const known = new Set(
        (presets || [])
            .map(reason => String(reason?.ReasonText || '').trim().toLowerCase())
            .filter(Boolean),
    )
    if (!known.size) return null
    return [...notes].reverse().find(note => {
        if (!note || note.IsInternal || note.IsComment) return false
        const parts = String(note.Notes || '')
            .split('; ')
            .map(part => part.trim().toLowerCase())
            .filter(Boolean)
        return parts.length > 0 && parts.every(part => known.has(part))
    })
}

export const noteIdOf = note => note?.NoteId ?? note?.Id ?? null
